/**
 * src/components/AgentMarkerLayer.tsx
 *
 * Renders one Mapbox marker per visible AI agent, kept in sync with
 * the live `/ws/agents` feed (with a polled `/api/v2/agents/movement
 * /by-country` warm-up + fallback when the WS is offline).
 *
 * Architecture
 * ------------
 *   1. On mount, snapshot the country's agents via REST (so the map
 *      shows everyone present even before the first WS frame arrives).
 *   2. Open a `/ws/agents` subscription filtered by country.
 *   3. Each `agent_position_update` either upserts or animates an
 *      existing marker; `agent_offline` removes it.
 *   4. If the WS upstream goes 'disconnected', re-poll every 10 s
 *      until WS comes back (so we don't show a frozen map).
 *
 * The marker DOM element is a self-contained pill ("🤖" + display
 * name when available) so it's visually distinct from the existing
 * wallet markers.  Clicking it fires `onAgentSelected(agent_pk)` —
 * App.tsx wires that to `openWalletProfileFromMarker` so the
 * existing profile-overlay flow handles AI agents transparently
 * (it already uses `AgentBadge` to indicate the wallet is an agent).
 *
 * Avatar / displayName
 *   GeoLink leaves these as null per the slice-3 spec.  The component
 *   resolves them lazily via `getPublicProfile()` on first sighting
 *   and caches in a ref so we don't refetch.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';

import {
  listAgentsByCountry,
  openAgentFeed,
  type AgentFeedFrame,
  type AgentFeedHandle,
  type AgentListEntry,
} from '../services/agentPositionService';
import { getPublicProfile } from '../services/profileService';

interface MarkerRecord {
  marker:        mapboxgl.Marker;
  el:            HTMLDivElement;
  name:          string | null;
  avatarUrl:     string | null;
  lastUpdateAt:  string;
  isStale:       boolean;
}

interface AgentMarkerLayerProps {
  /** Live mapbox map instance (parent owns lifecycle). */
  map:         mapboxgl.Map | null;
  /**
   * The agent currently in "teleport mode focus". When set, that
   * marker is highlighted (brighter border + pulse halo) and every
   * other agent marker is dimmed so the user can keep visual track
   * of who they're following. Restyling is done by toggling a
   * `data-selected` attribute on the existing marker DOM — no
   * marker is destroyed/recreated, so animations on other markers
   * (smooth setLngLat) keep playing without flicker.
   */
  selectedAgentId?: string | null;
  /**
   * ISO 3166-1 alpha-2 country (or list of countries).
   *   - `null`        → dormant (no subscriptions, no markers).
   *   - `'US'`        → original behaviour: one country only.
   *   - `['US','CA']` → multi-country: open one /ws/agents subscription
   *                     per country and merge results into a single
   *                     marker map keyed by `agent_pk`. Order is
   *                     irrelevant (we deduplicate + sort internally),
   *                     so callers can rebuild the array on every
   *                     render without thrashing the WS connections.
   *
   * Existing single-string callers don't need to change — the
   * component normalises both shapes the same way.
   */
  country:     string | string[] | null;
  /** Optional click handler; passed the agent's G-address. */
  onAgentSelected?: (agentPk: string) => void;
  /** Optional include-stale toggle (default false — match GeoLink). */
  includeStale?: boolean;
  /**
   * Fires whenever the merged positions Map changes (any country) —
   * lets the parent surface a "AI agents (N)" pill without
   * duplicating the WS subscription. The array is a fresh snapshot
   * on every change so callers can `setState(arr)` safely.
   */
  onPositionsChange?: (positions: AgentListEntry[]) => void;
  /**
   * Per-agent position OVERRIDE. When an entry exists for a given
   * agent_pk, the marker for that agent renders at the override
   * coordinates instead of the live WS-reported position.
   *
   * Use case: AI-agent teleport mode. While the cinematic camera is
   * flying through a queue of stops, we want the marker to lag the
   * live WS feed and only "snap" to each new stop when the camera
   * arrives. That keeps the marker, the camera, and every viewer's
   * camera locked to the same beat. As soon as teleport mode exits
   * the override goes back to `null`/empty and the marker resumes
   * tracking the live WS feed.
   *
   * Implementation: this is plumbed through to the same per-render
   * upsert loop that handles WS positions, so overrides + live
   * positions share one code path (one `setLngLat` per marker per
   * frame; no flicker, no marker recreation).
   */
  positionOverrides?: Map<string, { lat: number; lng: number }> | null;
  /**
   * Set of agent IDs currently live-broadcasting (i.e. their
   * `broadcasterId` appears in an active `LiveSession` with
   * `status === 'live'`). When an agent is in this Set we paint a
   * pulsing red ring around their marker (mirroring the human
   * wallet-marker pulse used by `.other-user-marker.live`).
   *
   * Toggled on existing markers via a `data-live` attribute (no
   * marker DOM is recreated) so the smooth `setLngLat` animation
   * keeps playing across broadcast start / stop transitions
   * without flicker.
   */
  liveAgentIds?: Set<string> | null;
}

const REPOLL_MS_WHEN_DISCONNECTED = 10_000;

/**
 * Hook: subscribe to agent positions in one or more countries. Returns a
 * Map of `agent_pk` → live position (single global Map across countries —
 * markers dedupe automatically because the upsert key is `agent_pk`),
 * plus an aggregate upstream state for diagnostics.
 *
 * Multi-country fan-out: opens one `/ws/agents` subscription per
 * country (the server already supports per-country filtering — there
 * is no aggregate "watch list" frame) and merges every frame into the
 * same positions Map. The same applies to the warm-up snapshot via
 * `Promise.all(listAgentsByCountry(...))` so the very first paint
 * already shows everyone before any WS frame lands.
 */
function useAgentPositions(countries: string[], includeStale = false): {
  positions: Map<string, AgentListEntry>;
  upstreamState: 'connected' | 'connecting' | 'disconnected';
} {
  const [positions, setPositions]         = useState<Map<string, AgentListEntry>>(() => new Map());
  const [upstreamState, setUpstreamState] = useState<'connected' | 'connecting' | 'disconnected'>('connecting');
  const handlesRef                        = useRef<AgentFeedHandle[]>([]);
  const pollTimerRef                      = useRef<ReturnType<typeof setInterval> | null>(null);

  // Order-independent stable key for the dependency array. Without
  // this, `['US','CA']` and `['CA','US']` (or any new array reference
  // with the same contents) would tear down + rebuild every WS
  // subscription on every render, which would also drop in-flight
  // snapshot results and flash the markers off → on for the user.
  const countriesKey = useMemo(
    () => [...countries].map((c) => c.toUpperCase()).sort().join(','),
    [countries],
  );

  useEffect(() => {
    if (countriesKey === '') {
      setPositions(new Map());
      return;
    }
    const watch = countriesKey.split(',');
    let cancelled = false;

    const refreshSnapshot = async () => {
      try {
        const snaps = await Promise.all(
          watch.map((c) => listAgentsByCountry({ country: c, includeStale })),
        );
        if (cancelled) return;
        setPositions((prev) => {
          // Merge every country's snapshot into a single Map keyed by
          // agent_pk. We preserve any newer in-memory entries (WS frames
          // that arrived while the snapshots were in flight) by
          // comparing broadcast_seq.
          const next = new Map(prev);
          for (let i = 0; i < snaps.length; i += 1) {
            const snap = snaps[i];
            const stampedCountry = (watch[i] || snap.country || '').toUpperCase() || null;
            for (const a of snap.agents) {
              const existing = next.get(a.agent_pk);
              if (!existing
                || (a.broadcast_seq != null
                    && existing.broadcast_seq != null
                    && a.broadcast_seq > existing.broadcast_seq)) {
                next.set(a.agent_pk, {
                  ...a,
                  country_code: a.country_code || stampedCountry,
                });
              }
            }
          }
          return next;
        });
      } catch {
        // Best-effort snapshot — fall back to whatever the WS has.
      }
    };

    refreshSnapshot();

    handlesRef.current = watch.map((c) => openAgentFeed({
      initialSubs: [{ feed: 'agents', country: c }],
      onFrame: (frame: AgentFeedFrame) => {
        if (cancelled) return;
        if (frame.type === 'upstream_state') {
          // Multiple subscriptions emit independent upstream-state
          // frames. We just adopt the latest one — when ANY feed flips
          // to disconnected it starts the snapshot poller, and when the
          // next one reports connected it tears it back down. Good
          // enough for diagnostics; the component otherwise doesn't
          // surface this state to the user.
          setUpstreamState(frame.state);
          if (frame.state === 'disconnected' && !pollTimerRef.current) {
            pollTimerRef.current = setInterval(refreshSnapshot, REPOLL_MS_WHEN_DISCONNECTED);
          }
          if (frame.state === 'connected' && pollTimerRef.current) {
            clearInterval(pollTimerRef.current);
            pollTimerRef.current = null;
            void refreshSnapshot();
          }
          return;
        }
        if (frame.type === 'agent_position_update') {
          // The server already filters per-subscription, but a frame
          // could in theory carry a country we're not watching (e.g.
          // future server-side broadcast feeds). Drop those rather
          // than letting them pollute the merged Map.
          if (frame.country_code && !watch.includes(frame.country_code.toUpperCase())) return;
          setPositions((prev) => {
            const next = new Map(prev);
            const existing = next.get(frame.agent_pk);
            // Drop out-of-order frames.
            if (existing
              && existing.broadcast_seq != null
              && frame.broadcast_seq != null
              && frame.broadcast_seq <= existing.broadcast_seq) {
              return prev;
            }
            next.set(frame.agent_pk, {
              agent_pk:       frame.agent_pk,
              lat:            frame.lat,
              lng:            frame.lng,
              displayName:    frame.displayName ?? existing?.displayName ?? null,
              avatarUrl:      frame.avatarUrl   ?? existing?.avatarUrl   ?? null,
              is_stale:       false,
              broadcast_seq:  frame.broadcast_seq,
              last_update_at: frame.ts,
              country_code:   (frame.country_code || existing?.country_code || null)?.toUpperCase() || null,
            });
            return next;
          });
          return;
        }
        if (frame.type === 'agent_offline') {
          setPositions((prev) => {
            if (!prev.has(frame.agent_pk)) return prev;
            const next = new Map(prev);
            // Default (includeStale=false) treats offline as "remove
            // from the map immediately" — matches the operator-
            // intuitive behaviour when the movement loop is stopped.
            // Callers that explicitly want stale agents to linger
            // (debug overlays, etc.) opt in via includeStale=true,
            // which keeps the marker but greys it out.
            if (!includeStale) {
              next.delete(frame.agent_pk);
              return next;
            }
            const existing = next.get(frame.agent_pk);
            if (existing) next.set(frame.agent_pk, { ...existing, is_stale: true });
            return next;
          });
        }
      },
    }));

    return () => {
      cancelled = true;
      for (const h of handlesRef.current) {
        try { h.close(); } catch { /* swallow */ }
      }
      handlesRef.current = [];
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, [countriesKey, includeStale]);

  return { positions, upstreamState };
}

// ── Marker rendering ────────────────────────────────────────────────────────

function makeMarkerElement(
  agentPk:    string,
  displayName: string | null,
  avatarUrl:   string | null,
  isStale:     boolean,
  isLive:      boolean,
  onClick:     (agentPk: string) => void,
): HTMLDivElement {
  const el = document.createElement('div');
  el.className = 'gt-agent-marker';
  // `data-live` flips the pulsing red broadcast ring (CSS injected
  // once below). We set it pre-paint so the FIRST frame of a marker
  // already shows the ring — important for the case where a viewer
  // joins mid-session and the agent is already live; if we waited
  // until the data-live sync useEffect ran we'd flash an unstyled
  // marker for one frame.
  if (isLive) el.setAttribute('data-live', 'true');
  el.style.cssText = [
    'display:inline-flex',
    'align-items:center',
    'gap:4px',
    'padding:3px 7px',
    'border-radius:14px',
    'font:600 11px system-ui, -apple-system, sans-serif',
    'cursor:pointer',
    'pointer-events:auto',
    'box-shadow:0 1px 4px rgba(0,0,0,0.35)',
    'border:1.5px solid #6366f1',
    isStale ? 'background:rgba(40,40,50,0.85);color:#9ca3af' : 'background:#1f2937;color:#e0e7ff',
    isStale ? 'opacity:0.65' : 'opacity:1',
    'transition:transform 280ms ease, opacity 200ms ease',
    'transform-origin:center bottom',
  ].join(';');

  const icon = document.createElement('span');
  icon.textContent = '🤖';
  icon.setAttribute('aria-hidden', 'true');
  icon.style.fontSize = '12px';
  el.appendChild(icon);

  if (avatarUrl) {
    const img = document.createElement('img');
    img.src = avatarUrl;
    img.alt = '';
    img.style.cssText = 'width:14px;height:14px;border-radius:50%;object-fit:cover';
    img.referrerPolicy = 'no-referrer';
    img.onerror = () => { try { img.remove(); } catch { /* swallow */ } };
    el.appendChild(img);
  }

  const label = document.createElement('span');
  const name = displayName && displayName.trim()
    ? displayName.trim().slice(0, 20)
    : `${agentPk.slice(0, 4)}…${agentPk.slice(-4)}`;
  label.textContent = name;
  el.appendChild(label);

  el.title = `AI agent ${agentPk}`;
  el.setAttribute('role', 'button');
  el.setAttribute('tabindex', '0');
  el.addEventListener('click', (evt) => {
    evt.stopPropagation();
    try { onClick(agentPk); } catch { /* swallow */ }
  });
  el.addEventListener('keydown', (evt) => {
    if (evt.key === 'Enter' || evt.key === ' ') {
      evt.preventDefault();
      try { onClick(agentPk); } catch { /* swallow */ }
    }
  });

  return el;
}

const AgentMarkerLayer: React.FC<AgentMarkerLayerProps> = ({
  map,
  country,
  onAgentSelected,
  includeStale = false,
  onPositionsChange,
  selectedAgentId = null,
  positionOverrides = null,
  liveAgentIds = null,
}) => {
  // Normalise the prop into a deduped, uppercase string[] so the hook
  // only ever has to deal with one shape. `string | string[] | null`
  // covers every existing caller without forcing them to update.
  const countries = useMemo(() => {
    if (!country) return [];
    const arr = Array.isArray(country) ? country : [country];
    return Array.from(new Set(arr.map((c) => (c || '').toUpperCase()))).filter(Boolean);
  }, [country]);
  const { positions, upstreamState } = useAgentPositions(countries, includeStale);
  const markersRef    = useRef<Map<string, MarkerRecord>>(new Map());
  const profileCache  = useRef<Map<string, { displayName: string | null; avatarUrl: string | null }>>(new Map());

  // Lazy-load missing displayName/avatarUrl from geo-profile.
  useEffect(() => {
    if (!map) return;
    let cancelled = false;
    const targets = Array.from(positions.values()).filter((a) =>
      (!a.displayName || !a.avatarUrl) && !profileCache.current.has(a.agent_pk),
    );
    if (targets.length === 0) return;

    (async () => {
      for (const a of targets) {
        if (cancelled) return;
        try {
          const p = await getPublicProfile(a.agent_pk);
          profileCache.current.set(a.agent_pk, {
            displayName: p?.displayName || a.displayName || null,
            avatarUrl:   p?.avatarUrl   || a.avatarUrl   || null,
          });
        } catch {
          profileCache.current.set(a.agent_pk, {
            displayName: a.displayName || null,
            avatarUrl:   a.avatarUrl   || null,
          });
        }
      }
      if (!cancelled) {
        // Trigger re-render by nudging state — but we don't have local
        // state for the cache, so just re-call sync block via a marker
        // refresh (this effect re-runs whenever positions change anyway).
      }
    })();

    return () => { cancelled = true; };
  }, [positions, map]);

  // Sync the markersRef with the current positions Map.
  useEffect(() => {
    if (!map) return;
    const live = markersRef.current;

    // Remove markers not in `positions` anymore.
    for (const [pk, rec] of Array.from(live.entries())) {
      if (!positions.has(pk)) {
        try { rec.marker.remove(); } catch { /* swallow */ }
        live.delete(pk);
      }
    }

    // Upsert / move markers that ARE in `positions`.
    for (const a of Array.from(positions.values())) {
      const cachedProfile = profileCache.current.get(a.agent_pk);
      const displayName = a.displayName ?? cachedProfile?.displayName ?? null;
      const avatarUrl   = a.avatarUrl   ?? cachedProfile?.avatarUrl   ?? null;

      // Apply per-agent position override (set by teleport mode for
      // the selected agent). When present we render at the override
      // coords; otherwise we use the live WS position. Because the
      // override flows through the SAME setLngLat call below, the
      // smooth marker animation works for cinematic-driven moves
      // exactly like it does for raw WS frames.
      const ov = positionOverrides?.get(a.agent_pk) ?? null;
      const renderLat = ov?.lat ?? a.lat;
      const renderLng = ov?.lng ?? a.lng;

      const isLive = !!(liveAgentIds && liveAgentIds.has(a.agent_pk));
      const existing = live.get(a.agent_pk);
      if (!existing) {
        const el = makeMarkerElement(
          a.agent_pk,
          displayName,
          avatarUrl,
          a.is_stale,
          isLive,
          (pk) => { onAgentSelected?.(pk); },
        );
        try {
          const marker = new mapboxgl.Marker({ element: el, anchor: 'bottom' })
            .setLngLat([renderLng, renderLat])
            .addTo(map);
          live.set(a.agent_pk, {
            marker,
            el,
            name:         displayName,
            avatarUrl,
            lastUpdateAt: a.last_update_at || '',
            isStale:      a.is_stale,
          });
        } catch {
          /* map not ready — try again next render */
        }
        continue;
      }

      // Existing marker: move it (with a smooth setLngLat) and refresh
      // the inner DOM if name / avatar / staleness changed.
      try { existing.marker.setLngLat([renderLng, renderLat]); } catch { /* swallow */ }

      const newName  = displayName ?? existing.name;
      const newAvatar = avatarUrl   ?? existing.avatarUrl;
      const newStale = a.is_stale;

      if (newName !== existing.name || newAvatar !== existing.avatarUrl || newStale !== existing.isStale) {
        const fresh = makeMarkerElement(
          a.agent_pk,
          newName,
          newAvatar,
          newStale,
          isLive,
          (pk) => { onAgentSelected?.(pk); },
        );
        existing.el.replaceWith(fresh);
        existing.el        = fresh;
        existing.name      = newName;
        existing.avatarUrl = newAvatar;
        existing.isStale   = newStale;
      } else {
        // Liveness toggled WITHOUT name/avatar/stale change → flip
        // the data-live attribute in place. Doing it here in the
        // upsert loop covers the case where a marker is being
        // re-rendered for any reason (position move, etc.) AND a
        // broadcast just started/stopped — without this fall-through
        // we'd have to wait for the dedicated liveAgentIds sync
        // effect to run on the next tick.
        if (isLive) existing.el.setAttribute('data-live', 'true');
        else        existing.el.removeAttribute('data-live');
      }
      existing.lastUpdateAt = a.last_update_at || existing.lastUpdateAt;
    }
  }, [positions, map, onAgentSelected, positionOverrides, liveAgentIds]);

  // Bubble the merged positions snapshot up so the parent can render
  // a sibling "AI agents (N)" pill (and/or apply a distance filter
  // to a player's current location). We emit a fresh array on every
  // change so callers can `setState(arr)` without aliasing concerns.
  useEffect(() => {
    if (!onPositionsChange) return;
    onPositionsChange(Array.from(positions.values()));
  }, [positions, onPositionsChange]);

  // ── Selection styling (no marker DOM is recreated) ───────────────────
  // Inject a global stylesheet once. We intentionally don't tie this
  // to a `useEffect` cleanup — the rules are tiny, idempotent, and
  // shared with any future caller of this layer.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (document.getElementById('gt-agent-marker-styles')) return;
    const style = document.createElement('style');
    style.id = 'gt-agent-marker-styles';
    style.textContent = `
      .gt-agent-marker[data-dimmed="true"]   { opacity: 0.28 !important; filter: grayscale(0.4); }
      .gt-agent-marker[data-selected="true"] {
        opacity: 1 !important;
        filter: none !important;
        transform: scale(1.18);
        border-color: #fbbf24 !important;
        box-shadow:
          0 0 0 3px rgba(251, 191, 36, 0.35),
          0 2px 8px rgba(0, 0, 0, 0.45) !important;
        z-index: 5;
      }
      .gt-agent-marker[data-selected="true"]::before {
        content: "";
        position: absolute;
        inset: -10px;
        border-radius: 999px;
        border: 2px solid rgba(251, 191, 36, 0.55);
        animation: gt-agent-pulse 1.6s ease-out infinite;
        pointer-events: none;
      }
      @keyframes gt-agent-pulse {
        0%   { transform: scale(0.85); opacity: 0.9; }
        70%  { transform: scale(1.45); opacity: 0;   }
        100% { transform: scale(1.45); opacity: 0;   }
      }

      /* Live-broadcast ring. Coexists with the selected ring (which
         uses ::before) by living on ::after, with a slightly larger
         inset so the two halos render concentrically — selected =
         inner amber ring, live = outer red ring. The ring is the
         same red shade and rhythm as the human wallet-marker pulse
         (.other-user-marker.live in App.css) so the "this person /
         agent is live" affordance is visually unified across both
         marker styles. */
      .gt-agent-marker[data-live="true"] {
        border-color: #ef4444 !important;
        box-shadow:
          0 0 0 2px rgba(239, 68, 68, 0.45),
          0 2px 10px rgba(0, 0, 0, 0.4) !important;
      }
      .gt-agent-marker[data-live="true"]::after {
        content: "";
        position: absolute;
        inset: -8px;
        border-radius: 999px;
        border: 2px solid #ef4444;
        animation: gt-agent-live-pulse 1.6s ease-out infinite;
        pointer-events: none;
        z-index: -1;
      }
      @keyframes gt-agent-live-pulse {
        0%   { transform: scale(0.85); opacity: 0.9;  }
        70%  { opacity: 0.15;                          }
        100% { transform: scale(2.1);  opacity: 0;    }
      }
    `;
    document.head.appendChild(style);
  }, []);

  // Whenever the selection (or the marker set) changes, walk the live
  // markers once and toggle the selection / dim attributes. Cheap —
  // O(N) where N is the number of agents on screen — and crucially
  // does NOT recreate any marker DOM, so the smooth setLngLat
  // animation on every other marker keeps playing through selection
  // changes without flicker.
  useEffect(() => {
    const live = markersRef.current;
    if (live.size === 0) return;
    if (selectedAgentId == null) {
      for (const rec of Array.from(live.values())) {
        rec.el.removeAttribute('data-selected');
        rec.el.removeAttribute('data-dimmed');
      }
      return;
    }
    for (const [pk, rec] of Array.from(live.entries())) {
      if (pk === selectedAgentId) {
        rec.el.setAttribute('data-selected', 'true');
        rec.el.removeAttribute('data-dimmed');
      } else {
        rec.el.removeAttribute('data-selected');
        rec.el.setAttribute('data-dimmed', 'true');
      }
    }
  }, [selectedAgentId, positions]);

  // Live-broadcast pulse sync. Walks the live markers once whenever
  // the live-broadcaster Set changes (typically every 15s when the
  // upstream useLiveFeed poll completes) and toggles the data-live
  // attribute. Mirrors the selection-sync pattern above so we avoid
  // recreating any marker DOM — the pulse appears/disappears via a
  // pure CSS attribute selector with no flicker.
  useEffect(() => {
    const live = markersRef.current;
    if (live.size === 0) return;
    const set = liveAgentIds || null;
    for (const [pk, rec] of Array.from(live.entries())) {
      const isLive = !!(set && set.has(pk));
      if (isLive) rec.el.setAttribute('data-live', 'true');
      else        rec.el.removeAttribute('data-live');
    }
  }, [liveAgentIds, positions]);

  // Cleanup all markers on unmount.
  useEffect(() => {
    const liveAtMount = markersRef.current;
    return () => {
      for (const rec of Array.from(liveAtMount.values())) {
        try { rec.marker.remove(); } catch { /* swallow */ }
      }
      liveAtMount.clear();
    };
  }, []);

  // Optional debug flag — exposed via JSX comment so React DevTools
  // shows the upstream state without us rendering anything visible.
  const _debug = useMemo(() => ({
    upstreamState,
    activeMarkers: positions.size,
  }), [upstreamState, positions]);
  void _debug;

  return null;
};

export default AgentMarkerLayer;
export { useAgentPositions };
export type { AgentListEntry };
